import { useCallback, useEffect, useRef, useState } from "react";

import { useAtom } from "jotai";

import { Box, Checkbox, Flex, Link, Show } from "@chakra-ui/react";

import { ClassesEnum } from "@ts/enums/ClassesEnum";

import {
    checkoutAtom,
    checkoutTotalAmountAtom,
    couponAtom,
    createSubscriptionAtom,
    isNewsletterSubEnabledAtom,
    isPaymentPendingAtom,
    pageQueryParamsAtom
} from "@atoms/checkoutAtom";

import { formatPrice, getTrackingId } from "@utils/utils";

import { Alert } from "@components/Alert";
import { Button } from "@components/Button";
import { ReCaptcha } from "@components/ReCaptcha";
import { Text } from "@components/Text";

import { CardPaymentContainer } from "./containers/CardPaymentContainer";
import { CardPaymentContainerRef } from "./containers/CardPaymentContainer/CardPaymentContainer";

declare function route(name: string, params?: object): string;

export interface PriceIdApiInterface {
    item_price_id: string;
    item_type: "plan" | "addon";
}

interface PropTypes {
    isHundredPercentOff: boolean;
}

const PaymentCaptureContainer = ({
    isHundredPercentOff = false
}: PropTypes) => {
    const cardPaymentContainerRef = useRef<null | CardPaymentContainerRef>(
        null
    );

    const [priceIds, setPriceIds] = useState<PriceIdApiInterface[]>([]);
    const [isTermsSelected, setIsTermsSelected] = useState<boolean>(false);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [dataLayerTracking, setDataLayerTracking] = useState({});
    const [isPaymentEnabled, setIsPaymentEnabled] = useState(false);
    const [paymentError, setPaymentError] = useState<{
        hasError: boolean;
        message: string;
    }>({ hasError: false, message: "" });
    const [heapData, setHeapData] = useState({});

    const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);

    const [checkout] = useAtom(checkoutAtom);
    const [coupon] = useAtom(couponAtom);
    const [createSubscriptionState] = useAtom(createSubscriptionAtom);
    const [setCheckoutTotal] = useAtom(checkoutTotalAmountAtom);
    const [isPaymentPending] = useAtom(isPaymentPendingAtom);
    const [isNewsletterSubEnabled, setIsNewsletterSubEnabled] = useAtom(
        isNewsletterSubEnabledAtom
    );

    const onInitiatePayment = async () => {
        await cardPaymentContainerRef.current!.initiateCardPayment();
    };

    const onPushDataLayerItem = useCallback(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerTracking);
    }, [dataLayerTracking]);

    const onCaptchVerified = (r: boolean) => setIsCaptchaVerified(r);

    const onToggleTermsAcceptance = (v: boolean) => setIsTermsSelected(v);

    const onTogglePaymentEnable = (isEnabled: boolean) =>
        setIsPaymentEnabled(isEnabled);

    const onSetPaymentError = (error: { hasError: boolean; message: string }) =>
        setPaymentError(error);

    const trackWithHeap = useCallback(
        (eventName: string) => {
            if (!!window.heap?.track && !!heapData) {
                window.heap.track(eventName, heapData);
            }
        },
        [heapData]
    );

    useEffect(() => {
        const preparePriceIds = () => {
            const priceIds: PriceIdApiInterface[] = [];
            if (checkout.plan) {
                priceIds.push({
                    item_price_id: checkout.plan.priceId,
                    item_type: "plan"
                });

                /**
                 * Check to see if the selected plan has addons which are included by default.
                 */
                const defaultIncludedAddon = checkout.plan.addons
                    ? checkout.plan.addons.find(a => a.isIncluded) ?? null
                    : null;
                if (defaultIncludedAddon) {
                    priceIds.push({
                        item_price_id: defaultIncludedAddon.priceId,
                        item_type: "addon"
                    });
                }
            }
            if (checkout.addon) {
                priceIds.push({
                    item_price_id: checkout.addon.priceId,
                    item_type: "addon"
                });
            }
            setPriceIds(priceIds);
        };

        const prepareDataLayerItem = () => {
            const transactionProducts = [];
            if (checkout.plan) {
                transactionProducts.push({
                    item_id: checkout.plan.id,
                    item_name: checkout.plan.name,
                    item_category: "plan",
                    item_grade: checkout.grade!.name,
                    item_state: pageQueryParams.locationState,
                    price: checkout.plan.price,
                    quantity: 1
                });

                if (checkout.addon) {
                    transactionProducts.push({
                        item_id: checkout.addon.id,
                        item_name: checkout.addon.name,
                        item_category: "addon",
                        item_grade: checkout.grade!.name,
                        item_state: pageQueryParams.locationState,
                        price: checkout.addon.price,
                        quantity: 1
                    });
                }

                const trackingData = {
                    event: "purchase",
                    ecommerce: {
                        transaction_id: checkout.productFamily!.name,
                        value: setCheckoutTotal,
                        tax: 0,
                        shipping: 0,
                        currency: checkout.plan!.currency,
                        coupon,
                        items: transactionProducts
                    }
                };
                setDataLayerTracking(trackingData);
                // Setting Heap data via useState.
                const heapData = {
                    transaction_id: checkout.productFamily!.name,
                    value: setCheckoutTotal,
                    tax: 0,
                    shipping: 0,
                    currency: checkout.plan!.currency,
                    coupon,
                    items: transactionProducts
                };
                setHeapData(heapData);
            }
        };

        preparePriceIds();
        prepareDataLayerItem();
    }, [checkout, setCheckoutTotal]);

    return (
        <Flex
            bg="white"
            direction="column"
            pt={[8, null, null, 28]}
            gap={8}
            align="start"
            px={[5, null, null, 18, 20]}
            w={["full", null, null, null, null, 700]}
            mr={[0, null, "auto"]}
        >
            <ReCaptcha
                isCaptchaVerified={isCaptchaVerified}
                onCaptchVerified={onCaptchVerified}
            />
            <CardPaymentContainer
                ref={cardPaymentContainerRef}
                isHundredPercentOff={isHundredPercentOff}
                coupon={coupon}
                priceIds={priceIds}
                onPushDataLayerItem={onPushDataLayerItem}
                trackWithHeap={trackWithHeap}
                onTogglePaymentEnable={onTogglePaymentEnable}
                onSetPaymentError={onSetPaymentError}
            />

            <Text fontSize={14}>
                Your personal data will be used to process your order, support
                your experience throughout this website, and for other purposes
                described in our
                <Link
                    href={route("external.ecom.privacy-policy")} // nosonar
                    color="#06F"
                    target="_blank"
                >
                    {" "}
                    privacy policy.
                </Link>
            </Text>
            <Checkbox
                className={ClassesEnum.Checkbox}
                data-tracking-id={getTrackingId(
                    [ClassesEnum.CardDetailsContainer, ClassesEnum.Checkbox],
                    "checkTermsAndConditions"
                )}
                size="md"
                onChange={e => onToggleTermsAcceptance(e.target.checked)}
            >
                I have read and agree to the website
                <Link
                    href={route("external.ecom.terms-conditions")} // nosonar
                    color="#06F"
                    target="_blank"
                >
                    {" "}
                    terms and conditions *
                </Link>
            </Checkbox>
            <Checkbox
                className={ClassesEnum.Checkbox}
                data-tracking-id={getTrackingId(
                    [ClassesEnum.CardDetailsContainer, ClassesEnum.Checkbox],
                    "checkNewsletterSubscription"
                )}
                defaultChecked={checkout.user.receiveOfferNews === true}
                onChange={() =>
                    setIsNewsletterSubEnabled(!isNewsletterSubEnabled)
                }
                size="md"
            >
                Receive free access to useful resources, tips and offers about
                online education
            </Checkbox>
            <Button
                className={ClassesEnum.Button}
                data-tracking-id={getTrackingId(
                    [ClassesEnum.CardDetailsContainer, ClassesEnum.Button],
                    "pay"
                )}
                isLoading={isPaymentPending}
                isDisabled={
                    !isTermsSelected ||
                    !isCaptchaVerified ||
                    createSubscriptionState.isSuccess ||
                    // If the payment method is a card payment, we check to see the status of the selected payment method
                    !isPaymentEnabled
                }
                loadingText="Processing. . ."
                width="full"
                onClick={onInitiatePayment}
                colorScheme="ebonyClay"
            >
                {isHundredPercentOff ? `Pay ${formatPrice(0, "")}` : "Pay"}
            </Button>
            {paymentError.hasError && (
                <Alert mt={-4} type="error" message={paymentError.message} />
            )}

            <Show below="md">
                <Box w="100%" minH="60px" />
            </Show>
        </Flex>
    );
};

export default PaymentCaptureContainer;
